import { logError } from '@/lib/sentry.lib';

import { useState } from 'react';
import type { UndefinedOnPartialDeep } from 'type-fest';
import { useShallow } from 'zustand/shallow';

import { useAppStore } from '@/store/app.store';
import { EMPTY_PENDING_SUBMISSIONS, useAssetEditStore } from '@/store/asset-edit.store';
import { useTimeWindowUpdateStore } from '@/store/time-window-update.store';
import type { Service } from '@repo/types/service';
import { invariant } from '@repo/utils/type.utils';
import { useQueryClient } from '@tanstack/react-query';

import { useAssetParams } from '@/hooks/asset.hooks';

import {
    buildPendingTimeWindowSubmissions,
    mergePendingTimeWindowSubmissions,
} from './pending-time-window-submissions.utils';
import type { useTimeWindowCandidates } from './time-window-candidates.hooks';
import { findTimeWindowById } from './time-window.utils';

type TimeWindowCandidates = ReturnType<typeof useTimeWindowCandidates>;

interface PendingTimeWindowCandidates {
    baselineOrderData: TimeWindowCandidates['baselineOrderData'];
    baselineServices: TimeWindowCandidates['baselineServices'];
    orderData: TimeWindowCandidates['orderData'];
    services: TimeWindowCandidates['services'];
    timeWindows: TimeWindowCandidates['timeWindows'];
}

interface UsePendingTimeWindowsParams {
    service: UndefinedOnPartialDeep<Service>;
    timeWindowCandidates: PendingTimeWindowCandidates;
    reasonCode: string;
    onSuccess?: (() => void) | undefined;
}

export const usePendingTimeWindows = ({
    service,
    timeWindowCandidates,
    reasonCode,
    onSuccess,
}: UsePendingTimeWindowsParams) => {
    const { baselineOrderData, baselineServices, orderData, services, timeWindows } = timeWindowCandidates;
    const { assetId } = useAssetParams();
    const queryClient = useQueryClient();
    const pendingSubmissions = useAssetEditStore((state) =>
        state.assetId === assetId ? state.pendingSubmissions : EMPTY_PENDING_SUBMISSIONS
    );
    const setPendingSubmissions = useAssetEditStore((state) => state.setPendingSubmissions);
    const { setSelectedReasonCode, setHasSubmitError } = useTimeWindowUpdateStore(
        useShallow((state) => ({
            setSelectedReasonCode: state.setSelectedReasonCode,
            setHasSubmitError: state.setHasSubmitError,
        }))
    );
    const isAssetDetailsEditMode = useAppStore((state) => state.assetDetails.mode === 'edit');
    const [isPending, setIsPending] = useState(false);
    const serviceId = service.serviceId;
    const isDeliveryTrigger = Boolean(service.isDelivery);

    const applyTimeWindowChange = async (selectedTimeWindowId: string) => {
        setIsPending(true);
        setHasSubmitError(false);

        try {
            invariant(baselineOrderData, 'Cannot update time window: baseline order data is missing');
            invariant(orderData, 'Cannot update time window: order data is missing');
            invariant(serviceId, 'Cannot update time window: service ID is missing');
            invariant(baselineOrderData.assetDetails.orderNo, 'Cannot update time window: order ID is missing');
            invariant(isAssetDetailsEditMode, 'Cannot update time window: asset details edit mode is inactive');

            const selectedTimeWindow = findTimeWindowById(timeWindows, selectedTimeWindowId);
            const submissionUpdatesById = await buildPendingTimeWindowSubmissions({
                baselineOrderData,
                baselineServices,
                orderData,
                queryClient,
                selectedTimeWindow,
                service,
                serviceId,
                services,
                isDeliveryTrigger,
                reasonCode,
            });

            setPendingSubmissions(
                assetId,
                mergePendingTimeWindowSubmissions({ pendingSubmissions, submissionUpdatesById })
            );
            setSelectedReasonCode('');
            onSuccess?.();
        } catch (error) {
            setHasSubmitError(true);
            logError(error);
        } finally {
            setIsPending(false);
        }
    };

    return {
        applyTimeWindowChange,
        isPending,
        isAssetDetailsEditMode,
    };
};
